import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import { useGetUserEntryQuery } from "services/auth/authService";
import { SchemaAssociations, associationTypes, associationState, idMap } from "types/carTypes";
import {
	formatTitle,
	scrollToTop,
	fetchAndMapAssociations,
	loadExistingData,
	createFormData,
} from "helpers/utils";
import PSPage from "components/globals/ui/layout/Page";
import { EntryLoading, EntryError } from "components/globals/EntryStates";
import { PSSelect } from "components/globals/ui/input/PSInputs";
import { ProgressBarCars, FormStage } from "components/globals/forms/ORSFormLayout";
import { useDispatch } from "react-redux";
import { updateEntry } from "store/authSlice";
import { ErrorPane } from "components/modals/Panes";
import { useNavigate } from "react-router-dom";

export default function CreateCarAssociations({ edit = false }) {
	const { id } = useParams();
	const [loading, setLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState(null);
	const [postData, updatePostData] = useState(SchemaAssociations);
	const userToken = useSelector((state) => state.auth.userToken);
	const dispatch = useDispatch();
	const navigate = useNavigate();

	const {
		data: existingEntryData,
		loading: entryLoading,
		error: entryError,
	} = useGetUserEntryQuery(`car/${id}`);

	const [associations, setAssociations] = useState(associationState);

	const updateAssociation = (type, value) => {
		setAssociations((prev) => ({ ...prev, [type]: value }));
	};

	// load existing data if editing
	useEffect(() => {
		if (existingEntryData) {
			loadExistingData(updatePostData, SchemaAssociations, existingEntryData);
			setLoading(false);
		}
	}, [existingEntryData]);



	// load all the association data into select boxes
	useEffect(() => {
		const loadAssociations = async () => {
			try {
				const results = await fetchAndMapAssociations(
					userToken,
					associationTypes
				);
				results.forEach(({ type, data }) => {
					updateAssociation(type, data);
				});
				setLoading(false);
			} catch (err) {
				setErrorMessage(err.message);
			}
		};

		loadAssociations();
	}, [userToken]);

	// coming from input onChange (text and select, type, category)
	const onInputChange = (context, val) => {
		setNewPostDataProperty(context, val);
	};


	const setNewPostDataProperty = (context, val) => {
		updatePostData((prevData) => ({
			...prevData,
			[context]: val,
		}));
	};

	// SUBMIT
	const submitForm = async () => {
		if (!userToken || !id) return;

		// Set initial state for loading, messages, etc.
		setLoading(true);
		setErrorMessage(null);

		let fd = createFormData(postData, SchemaAssociations);

		fd.append("internal_id", id);
	
		try {
			await dispatch(updateEntry({ data: fd, entry_type: "garagecar" })).unwrap();
			navigate(`/edit/car/mods/${id}`);
		} catch (err) {
			setErrorMessage(err.message || "Failed. Please try again.");
		} finally {
			setLoading(false);
			scrollToTop();
		}
	};

	const clearErrorMessage = () => {
		setErrorMessage(null);
	};

	if (entryLoading) {
		return <EntryLoading />;
	}
	if (entryError) {
		return <EntryError msg="Failed to load." />;
	}

	return (
		<PSPage>

			<ErrorPane
				visible={errorMessage}
				message={errorMessage}
				onClose={clearErrorMessage}
			/>

			<div className="relative">

				<ProgressBarCars stage={4} id={id} />


				<div className="mt-[60px]">

					{!loading && (
						<FormStage 
							heading={`Update garage car: ${existingEntryData?.entry?.title}`}
							subheading="Associations" 
							onSave={submitForm}
							stage={4}
							stages={5}
							previousUrl={`/edit/car/images/${id}`}
						>
							{associationTypes.length > 0 && !loading && (
								<>
									{associationTypes.map((association) => {
										const items = associations[association] || [];

										return (
											<div className="mb-6" key={association}>
												<PSSelect
													name={`${idMap[association]}_id`}
													label={association}
													firstItemLabel={`Associate with your ${association}`}
													value={postData[`${idMap[association]}_id`] || ""}
													onChange={onInputChange}
												>
													{items.map((item) => (
														<option value={item.internal_id} key={item.internal_id}>
															{association === "garage"
																? `${formatTitle(item.title)} (${item.year} ${
																		item.make
																	} ${item.model})`
																: formatTitle(item.title)}
														</option>
													))}
												</PSSelect>
											</div>
										);
									})}
								</>
							)}
						</FormStage>
					)}

				</div>
			</div>
		</PSPage>
	);
}
